/**
 * Managed SQL Database API Service
 *
 * API methods for managed SQL database operations including creation, updates,
 * access grants for applications and ELT jobs. Managed SQL DBs are PostgreSQL
 * databases in a shared Aurora cluster with IAM-enforced access control.
 * Updated to use standardized API response format.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from '../types/api.types';
import type {
  ManagedSqlDbSanitized,
  ManagedSqlDbCreateDTO,
  ManagedSqlDbUpdateDTO,
  ManagedSqlDbListQuery,
} from '../types/managed-sql-db.types';
import type { TwoFactorRequiredResponse } from '../types/auth.types';

// ============================================================================
// MANAGED SQL DATABASE SERVICE
// ============================================================================

export const managedSqlDbService = {
  /**
   * List all managed SQL databases for a tenant
   */
  listManagedSqlDbs: async (
    tenantId: string,
    query?: ManagedSqlDbListQuery
  ): Promise<ManagedSqlDbSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<ManagedSqlDbSanitized>>(
      `/tenants/${tenantId}/managed-sql-databases`,
      { params: query }
    );
    return response.data.data;
  },

  /**
   * Get managed SQL database by ID
   */
  getManagedSqlDb: async (
    tenantId: string,
    managedSqlDbId: string
  ): Promise<ManagedSqlDbSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<ManagedSqlDbSanitized>>(
      `/tenants/${tenantId}/managed-sql-databases/${managedSqlDbId}`
    );
    return response.data.data;
  },

  /**
   * Create a new managed SQL database
   */
  createManagedSqlDb: async (
    tenantId: string,
    data: ManagedSqlDbCreateDTO
  ): Promise<ManagedSqlDbSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<ManagedSqlDbSanitized>>(
      `/tenants/${tenantId}/managed-sql-databases`,
      data
    );
    return response.data.data;
  },

  /**
   * Update managed SQL database metadata
   * Note: Only name and description can be updated (code is immutable)
   */
  updateManagedSqlDb: async (
    tenantId: string,
    managedSqlDbId: string,
    data: ManagedSqlDbUpdateDTO
  ): Promise<ManagedSqlDbSanitized> => {
    const response = await apiClient.patch<ApiSuccessResponse<ManagedSqlDbSanitized>>(
      `/tenants/${tenantId}/managed-sql-databases/${managedSqlDbId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete managed SQL database
   * Requires 2FA verification. If no 2FA credentials provided, returns TwoFactorRequiredResponse.
   * @param tenantId - Tenant ID
   * @param managedSqlDbId - Managed SQL Database ID
   * @param verificationCodeId - Optional verification code ID for email 2FA
   * @param verificationCode - Optional verification code for email 2FA
   */
  deleteManagedSqlDb: async (
    tenantId: string,
    managedSqlDbId: string,
    verificationCodeId?: string,
    verificationCode?: string
  ): Promise<{ message: string } | TwoFactorRequiredResponse> => {
    const body = verificationCodeId && verificationCode
      ? { verificationCodeId, verificationCode }
      : undefined;

    const response = await apiClient.delete<ApiSuccessResponse<TwoFactorRequiredResponse> | ApiActionResponse>(
      `/tenants/${tenantId}/managed-sql-databases/${managedSqlDbId}`,
      body ? { data: body } : undefined
    );

    // Check if 2FA is required (response contains data with twoFactorRequired flag)
    if (response.data.data && 'twoFactorRequired' in response.data.data) {
      return response.data.data as TwoFactorRequiredResponse;
    }

    return { message: response.data.message };
  },
};

export default managedSqlDbService;
